import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import type { AddKeywordInput, GeoSelection, ImportKeywordsInput } from '../models/keyword.types'
import type {
  SeasonalityResponse,
  TrendSeasonalityRequestContext,
} from '../models/trendSeasonality.types'

import { appHttp } from '@/shared/http'

type MarketingInsightsHttp = Pick<AxiosInstance, 'get' | 'post'>

function requestConfig(
  filters: TrendSeasonalityRequestContext['filters'],
  signal?: AbortSignal,
): AxiosRequestConfig {
  return {
    params: { filters: JSON.stringify(filters) },
    suppressGlobalErrorHandler: true,
    meta: { suppressGlobalErrorHandler: true, timeoutMs: 120_000 },
    ...(signal ? { signal } : {}),
  }
}

function seasonalityResponse(value: unknown): SeasonalityResponse {
  if (typeof value !== 'object' || value === null) return { data: [] }
  const data = (value as { data?: unknown }).data
  if (Array.isArray(data)) return { data }
  if (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as { data?: unknown }).data)
  ) {
    return { data: (data as { data: unknown[] }).data }
  }
  return { data: [] }
}

/**
 * The two activation paths' URLs, spelled EXACTLY as the backend declares them (contract 25 §12,
 * BE-25-02). They disagree, and the disagreement is load-bearing:
 *
 *   `workspace/workspace_settings/urls.py:37`  `add-new-keyword/`      ← trailing slash
 *   `workspace/workspace_settings/urls.py:39`  `add-trends-keywords`   ← none
 *
 * Normalising them is not free. `TrailingSlashFallbackMiddleware` and Django's `APPEND_SLASH`
 * between them make both spellings *resolve*, but only via a **308 redirect** — and paying a
 * redirect on the multipart path means re-uploading the whole CSV body. The leading slash is the
 * one difference that is NOT observable: axios' `combineURLs` strips it when joining `baseURL`, so
 * v3's `accounts/...` and `/accounts/...` put identical bytes on the wire. It is kept verbatim
 * anyway so a reader diffing against v3 finds no unexplained edit.
 */
const ADD_KEYWORD_PATH = (accountId: number | string, workspaceId: number | string): string =>
  `/accounts/${accountId}/workspaces/${workspaceId}/settings/add-new-keyword/`
const IMPORT_KEYWORDS_PATH = (accountId: number | string, workspaceId: number | string): string =>
  `accounts/${accountId}/workspaces/${workspaceId}/settings/add-trends-keywords`

/**
 * `geo_location` on both paths (BE-25-04 / ORP-20189). The canonical shape is an array of
 * `criteria_id` integers, and **an absent key means "all countries"**.
 *
 * v3 sent `null` on the JSON path but the literal 4-character string `"null"` on the multipart one
 * (`TrendSeasonalityCard.vue:236-243`), because `FormData.append` stringifies its value — the two
 * paths disagreed on the same selection (D6). Both are expressed here by simply not writing the
 * key, which no serialiser can turn back into a value.
 */
function geoLocationValue(geo: GeoSelection): number[] | undefined {
  return geo.allCountries || geo.countryId === null ? undefined : [geo.countryId]
}

/** Writes surface their failure as a typed `ApiError`; the flow owns the copy, not the http layer. */
const WRITE_CONFIG: AxiosRequestConfig = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
}

/** Module 25's only app-api boundary. Axios params encode the JSON filter value. */
export class MarketingInsightsApi {
  constructor(private readonly http: MarketingInsightsHttp = appHttp) {}

  async getSeasonalityData(
    context: TrendSeasonalityRequestContext,
    signal?: AbortSignal,
  ): Promise<SeasonalityResponse> {
    const response = await this.http.get(
      `/accounts/${context.accountId}/workspaces/${context.workspaceId}/schedules/${context.scheduleId}/get_seasonality_data`,
      requestConfig(context.filters, signal),
    )
    return seasonalityResponse(response.data)
  }

  /** Activate one keyword (contract 25 §12 endpoint 2). */
  async addKeyword(
    accountId: number | string,
    workspaceId: number | string,
    input: AddKeywordInput,
  ): Promise<void> {
    const geoLocation = geoLocationValue(input.geo)
    await this.http.post(
      ADD_KEYWORD_PATH(accountId, workspaceId),
      {
        keyword: input.keyword,
        schedule_id: input.scheduleId,
        ...(geoLocation ? { geo_location: geoLocation } : {}),
      },
      WRITE_CONFIG,
    )
  }

  /**
   * Activate every unique value of one CSV column (contract 25 §12 endpoint 3).
   *
   * `language_constant` is deliberately NOT sent. v3 posted it on both paths
   * (`TrendSeasonalityCard.vue:150,242`) and neither view has ever read it: the backend removed
   * the axis wholesale in `b906c6e7b` (2025-12-09) and pinned Google Ads' `request.language` to
   * `None`. ORP-20190 ratified that removal, so the field is dead weight, not a gap.
   */
  async importKeywords(
    accountId: number | string,
    workspaceId: number | string,
    input: ImportKeywordsInput,
  ): Promise<void> {
    const body = new FormData()
    body.append('csv_file', input.file)
    body.append('column_name', input.columnName)
    body.append('schedule_id', String(input.scheduleId))
    const geoLocation = geoLocationValue(input.geo)
    // Appended as JSON, the shape the parser reads first. The key is absent for "all countries" —
    // `FormData` cannot express null, and every literal that tries (`"null"`, `"undefined"`, `""`)
    // is a string the backend has to guess at.
    if (geoLocation) body.append('geo_location', JSON.stringify(geoLocation))
    await this.http.post(IMPORT_KEYWORDS_PATH(accountId, workspaceId), body, WRITE_CONFIG)
  }
}

export const marketingInsightsApi = new MarketingInsightsApi()
